/**
 * Hero Component - Landing page hero section
 */

import { useEffect, useState } from 'react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

export default function Hero() {
    const [reducedMotion, setReducedMotion] = useState(false);

    useEffect(() => {
        const query = window.matchMedia('(prefers-reduced-motion: reduce)');
        setReducedMotion(query.matches);
        const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
        query.addEventListener('change', handler);
        return () => query.removeEventListener('change', handler);
    }, []);

    return (
        <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white py-20 sm:py-28">
            {/* Content */}
            <div className="relative w-full px-4 sm:px-6 lg:px-12 xl:px-20">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        {/* Left side - Content */}
                        <div className="text-left">
                            {/* Badge */}
                            <div
                                className="hero-animate-fade-up inline-flex items-center px-4 py-2 rounded-full bg-[#2ba6bb]/10 text-[#2ba6bb] text-sm font-bold mb-6 border border-[#2ba6bb]/30"
                            >
                                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
                                </svg>
                                AI Readiness: 100% In-Tenant, Zero-Trust
                            </div>

                            {/* Main heading */}
                            <h1
                                className="hero-animate-fade-up text-5xl sm:text-6xl lg:text-7xl font-black text-gray-900 mb-6 leading-none tracking-tight"
                                style={{ animationDelay: '90ms' }}
                            >
                                Is Your Intranet
                                <span className="block bg-gradient-to-r from-[#2ba6bb] to-[#16a8c5] bg-clip-text text-transparent mt-2">Copilot-Ready?</span>
                            </h1>
                            <p
                                className="hero-animate-fade-up text-xl sm:text-2xl text-gray-600 mb-8 leading-relaxed font-medium"
                                style={{ animationDelay: '180ms' }}
                            >
                                Instantly scan every SharePoint page for AI readiness: readability, structure, intent, image accessibility, keyword health and more. Analysed entirely within your Microsoft 365 tenant; your content never leaves your environment.
                            </p>

                            {/* Stats */}
                            <div
                                className="hero-animate-fade-up grid grid-cols-3 gap-6 mb-10"
                                style={{ animationDelay: '270ms' }}
                            >
                                <div>
                                    <div className="text-3xl sm:text-4xl font-black text-[#2ba6bb]">8</div>
                                    <div className="text-sm text-gray-600 mt-1 font-medium">Quality Checks</div>
                                </div>
                                <div>
                                    <div className="text-3xl sm:text-4xl font-black text-[#2ba6bb]">3</div>
                                    <div className="text-sm text-gray-600 mt-1 font-medium">Core Scores</div>
                                </div>
                                <div>
                                    <div className="text-3xl sm:text-4xl font-black text-[#2ba6bb]">100%</div>
                                    <div className="text-sm text-gray-600 mt-1 font-medium">In-Tenant</div>
                                </div>
                            </div>

                            {/* CTA Buttons */}
                            <div
                                className="hero-animate-fade-up flex flex-col sm:flex-row gap-4"
                                style={{ animationDelay: '360ms' }}
                            >
                                <a href="#get-started" className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-[#2ba6bb] to-[#16a8c5] text-white text-lg font-bold rounded-lg hover:from-[#228a9d] hover:to-[#138a9f] transition-all duration-200 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 focus:outline-none focus:ring-4 focus:ring-[#2ba6bb] focus:ring-opacity-50">
                                    Start Free Scan
                                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                    </svg>
                                </a>
                                <a href="#how-it-works" className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-700 text-lg font-bold rounded-lg hover:bg-gray-50 transition-all duration-200 border-2 border-gray-300 hover:border-[#2ba6bb] shadow-md hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-gray-300 focus:ring-opacity-50">
                                    See How It Works
                                </a>
                            </div>
                        </div>

                        {/* Right side - Illustration (smaller + top-margin on mobile, where it stacks below the CTAs) */}
                        <div className="relative mt-10 lg:mt-0">
                            <div className="absolute inset-0 -m-10 bg-gradient-to-br from-[#2ba6bb]/10 to-transparent rounded-full blur-3xl" aria-hidden="true" />

                            <div
                                className="hero-animate-fade-in-right relative mx-auto w-full max-w-sm sm:max-w-md lg:max-w-2xl aspect-[4/3] lg:aspect-[16/9]"
                                style={{ animationDelay: '150ms' }}
                            >
                                <DotLottieReact
                                    src="/lottie/dashboard-hero.json"
                                    loop={!reducedMotion}
                                    autoplay={!reducedMotion}
                                    layout={{ fit: 'cover', align: [0.5, 0.5] }}
                                    style={{ width: '100%', height: '100%' }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
